import React, {
  useState,
  createContext,
  useContext,
  useEffect,
  PropsWithChildren,
  useCallback,
} from "react";

import { getAllProvinceWithChild } from "./utils";

import { TCity, TJurisdiction, TProvince } from "types/dto";
import { TRegion } from "contexts/regions_v2/types";

type TCities = Array<TCity & { provinceId: string; provinceName: string }>;
type TJurisdictions = Array<
  TJurisdiction & {
    provinceId: string;
    provinceName: string;
    cityId: string;
    cityName: string;
  }
>;
type TRegionsContext = {
  provincesLoading: boolean;
  citiesLoading: boolean;
  jurisdictionsLoading: boolean;
  allProvinces: TProvince[];
  // availableCities: TCity[];
  availableCities: TCities;
  // availableJurisdictions: TJurisdiction[];
  availableJurisdictions: TJurisdictions;
  // allCities: TCity[];
  allCities: TCities;
  // allJurisdictions: TJurisdiction[];
  allJurisdictions: TJurisdictions;
  selectedRegion: TRegion;
  setSelectedRegion: React.Dispatch<React.SetStateAction<TRegion>>;
};
const initialData: TRegionsContext = {
  provincesLoading: true,
  citiesLoading: true,
  jurisdictionsLoading: true,
  allProvinces: [],
  availableCities: [],
  availableJurisdictions: [],
  allCities: [],
  allJurisdictions: [],
  selectedRegion: {
    city: undefined,
    province: undefined,
    jurisdiction: undefined,
  },
  setSelectedRegion: () => {},
};

export const RegionContext_v2 = createContext(initialData);

const useRegion_v2 = () => useContext(RegionContext_v2);

function RegionProvider_v2({ children }: PropsWithChildren) {
  const [provinces, setProvinces] = useState<TProvince[]>([]);
  const [isProvincesLoading, setIsProvincesLoading] = useState(true);
  // const [cities, setCities] = useState<TCity[]>([]);
  const [cities, setCities] = useState<TCities>([]);
  const [isCitiesLoading, setIsCitiesLoading] = useState(false);
  // const [jurisdictions, setJurisdictions] = useState<TJurisdiction[]>([]);
  const [jurisdictions, setJurisdictions] = useState<TJurisdictions>([]);
  const [isJurisdictionsLoading, setIsJurisdictionsLoading] = useState(false);
  // const [allCities, setAllCities] = useState<TCity[]>([]);
  const [allCities, setAllCities] = useState<TCities>([]);
  // const [allJurisdictions, setAllJurisdictions] = useState<TJurisdiction[]>([]);
  const [allJurisdictions, setAllJurisdictions] = useState<TJurisdictions>([]);
  const [selectedRegion, setSelectedRegion] = useState<TRegion>({
    city: undefined,
    jurisdiction: undefined,
    province: undefined,
  });

  const getProvincesData = useCallback(async () => {
    setIsProvincesLoading(true);
    const provincesData = await getAllProvinceWithChild();
    if (provincesData) {
      setProvinces(provincesData);
    }
    setIsProvincesLoading(false);
  }, []);

  const getCitiesData = useCallback(
    async (provinceId: string) => {
      setIsCitiesLoading(true);
      const province = provinces.find((p) => p.id === provinceId);
      const citiesData = province?.cities || [];
      if (citiesData.length > 0 && province) {
        setCities(
          citiesData.map((item) => {
            return {
              ...item,
              provinceId: province.id,
              provinceName: province.name,
            };
          })
        );
        // setCities({
        //   ...citiesData,
        //   provinceId: province?.id,
        //   provinceName: province?.name,
        // });
      }
      setIsCitiesLoading(false);
    },
    [provinces]
  );

  const getJurisdictionsData = useCallback(
    async (cityId: string) => {
      setIsJurisdictionsLoading(true);
      const city = cities.find((c) => c.id === cityId);
      const jurisdictionsData = city?.jurisdictions || [];
      if (jurisdictionsData.length > 0 && city) {
        setJurisdictions(
          jurisdictionsData.map((item) => {
            return {
              ...item,
              cityId: city.id,
              cityName: city.name,
              provinceId: city.provinceId,
              provinceName: city.provinceName,
            };
          })
        );
      }
      setIsJurisdictionsLoading(false);
    },
    [cities]
  );

  useEffect(() => {
    getProvincesData();
  }, []);

  useEffect(() => {
    if (selectedRegion.province) {
      getCitiesData(selectedRegion.province.id);
    } else {
      setCities([]);
    }
  }, [selectedRegion.province]);

  useEffect(() => {
    if (selectedRegion.city) {
      getJurisdictionsData(selectedRegion.city.id);
    } else {
      setJurisdictions([]);
    }
  }, [selectedRegion.city]);

  useEffect(() => {
    if (!isProvincesLoading && provinces.length > 0) {
      const allCitiesData: TCities = [];
      const allJurisdictionsData: TJurisdictions = [];
      provinces.forEach((province) => {
        province.cities.forEach((city) => {
          // allCitiesData.push(city);
          allCitiesData.push({
            ...city,
            provinceId: province.id,
            provinceName: province.name,
          });
          city.jurisdictions.forEach((jurisdiction) => {
            // allJurisdictionsData.push(jurisdiction);
            allJurisdictionsData.push({
              ...jurisdiction,
              cityId: city.id,
              cityName: city.name,
              provinceId: province.id,
              provinceName: province.name,
            });
          });
        });
      });
      setAllCities(allCitiesData);
      setAllJurisdictions(allJurisdictionsData);
    }
  }, [provinces, isProvincesLoading]);

  const value: TRegionsContext = {
    provincesLoading: isProvincesLoading,
    citiesLoading: isCitiesLoading,
    jurisdictionsLoading: isJurisdictionsLoading,
    allProvinces: provinces,
    availableCities: cities,
    availableJurisdictions: jurisdictions,
    allCities: allCities,
    allJurisdictions: allJurisdictions,
    selectedRegion,
    setSelectedRegion,
  };
  return (
    <RegionContext_v2.Provider value={value}>
      {children}
    </RegionContext_v2.Provider>
  );
}

export { RegionProvider_v2, useRegion_v2 };
